import { useCallback, useEffect, useState } from "react";
import { axiosGateway } from "../../api/axios";
import { MESSAGES_PATHS } from "../../api/endpoints";

const EMPTY = { blockedByMe: false, restrictedByMe: false, blockedByPeer: false };
const REFRESH_MS = 20000;

/**
 * Restriction / blocking state between the current user and `peerId`.
 * The backend is the source of truth; only doctors can change it
 * (`canManage`). A restriction set by the peer is never exposed here.
 */
export default function useChatRestriction(peerId, { canManage = false } = {}) {
  const peer = peerId ? String(peerId).replace(/^(DR_|PAT_)/i, "") : null;
  const [status, setStatus] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!peer) {
      setStatus(EMPTY);
      return;
    }
    try {
      const { data } = await axiosGateway.get(MESSAGES_PATHS.RESTRICTION_STATUS(peer), {
        withCredentials: true,
      });
      setStatus({ ...EMPTY, ...data });
    } catch {
      // Keep the last known state on transient errors.
    }
  }, [peer]);

  useEffect(() => {
    setStatus(EMPTY);
    refresh();
    if (!peer) return undefined;
    const timer = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(timer);
  }, [peer, refresh]);

  const setRestriction = useCallback(
    async (type) => {
      if (!canManage || !peer) return;
      setBusy(true);
      try {
        const { data } = await axiosGateway.put(
          MESSAGES_PATHS.RESTRICTION(peer),
          { type },
          { withCredentials: true }
        );
        setStatus({ ...EMPTY, ...data });
      } finally {
        setBusy(false);
      }
    },
    [canManage, peer]
  );

  const clearRestriction = useCallback(async () => {
    if (!canManage || !peer) return;
    setBusy(true);
    try {
      const { data } = await axiosGateway.delete(MESSAGES_PATHS.RESTRICTION(peer), {
        withCredentials: true,
      });
      setStatus({ ...EMPTY, ...data });
    } finally {
      setBusy(false);
    }
  }, [canManage, peer]);

  return { status, busy, refresh, setRestriction, clearRestriction };
}
